import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, User, Search, Menu, Sun, Moon, X } from "lucide-react";
import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

const nav = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/track", label: "Track" },
];

export default function Header() {
  const { cart, wishlist, user, theme, toggleTheme } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const cartCount = cart.reduce((a, b) => a + b.qty, 0);

  return (
    <>
      {/* Announcement */}
      <div className="bg-foreground text-background text-center py-2 label-xs" data-testid="promo-bar">
        FREE SHIPPING ACROSS INDIA · SECURE CHECKOUT · WHATSAPP SUPPORT
      </div>
      <header className="glass-header sticky top-0 z-40" data-testid="site-header">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          <nav className="hidden lg:flex items-center gap-8 flex-1">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `label-xs link-underline ${isActive ? "opacity-100" : "opacity-70 hover:opacity-100"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <Link to="/" data-testid="brand-logo" className="absolute left-1/2 -translate-x-1/2 select-none flex items-center gap-3">
            <Logo variant="monogram" className="h-9 w-9 md:h-10 md:w-10 dark:invert" loading="eager" />
            <div className="hidden sm:block">
              <div className="font-display text-sm md:text-base font-bold tracking-[0.32em] leading-none">THE FABRICS</div>
              <div className="text-[9px] tracking-[0.4em] opacity-60 mt-1.5">WEAR YOUR IDENTITY.</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 lg:gap-5 flex-1 justify-end">
            <button onClick={() => navigate("/shop")} className="hidden md:inline-flex" data-testid="search-btn" aria-label="Search"><Search className="h-4 w-4" /></button>
            <button onClick={toggleTheme} data-testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/wishlist" className="relative" data-testid="header-wishlist" aria-label="Wishlist">
              <Heart className="h-4 w-4" />
              {wishlist.length > 0 && <span className="absolute -top-2 -right-2 text-[10px] bg-foreground text-background w-4 h-4 grid place-items-center">{wishlist.length}</span>}
            </Link>
            <Link to={user ? "/account" : "/login"} data-testid="header-account" aria-label="Account"><User className="h-4 w-4" /></Link>
            <Link to="/cart" className="relative" data-testid="header-cart" aria-label="Cart">
              <ShoppingBag className="h-4 w-4" />
              {cartCount > 0 && <span className="absolute -top-2 -right-2 text-[10px] bg-foreground text-background w-4 h-4 grid place-items-center" data-testid="cart-badge">{cartCount}</span>}
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 bg-background lg:hidden" data-testid="mobile-drawer">
          <div className="p-6 flex justify-between items-center border-b">
            <div className="flex items-center gap-3">
              <Logo variant="monogram" className="h-7 w-7 dark:invert" />
              <span className="font-display font-bold tracking-[0.28em]">THE FABRICS</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
          </div>
          <nav className="p-8 space-y-6">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block font-serif text-3xl" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</Link>
            ))}
            <div className="pt-6 border-t space-y-4">
              {user ? (
                <Link to="/account" onClick={() => setOpen(false)} className="label-xs block">My Account</Link>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="label-xs block">Sign In</Link>
              )}
              <Link to="/wishlist" onClick={() => setOpen(false)} className="label-xs block">Wishlist ({wishlist.length})</Link>
              <Link to="/cart" onClick={() => setOpen(false)} className="label-xs block">Cart ({cartCount})</Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
